import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Alert,
  Empty,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import TagFormDrawer from "./TagFormDrawer";
import { triggerNotification } from "../../common/notify";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const DEFAULT_SORT_BY = "created_at";
const DEFAULT_SORT_ORDER = "desc";

const readInitialFilters = (searchParams) => ({
  search: searchParams.get("tgsearch") || "",
  sort_by: searchParams.get("tgsort") || DEFAULT_SORT_BY,
  sort_order: searchParams.get("tgorder") || DEFAULT_SORT_ORDER,
  page: Math.max(parseInt(searchParams.get("tgpage"), 10) || 1, 1),
  limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("tglimit"), 10))
    ? parseInt(searchParams.get("tglimit"), 10)
    : 10,
});

const TagsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [drawerState, setDrawerState] = useState({ open: false, mode: "create", id: null });

  // Sync filters → URL
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (filters.search) next.set("tgsearch", filters.search);
    else next.delete("tgsearch");
    next.set("tgsort", filters.sort_by);
    next.set("tgorder", filters.sort_order);
    next.set("tgpage", String(filters.page));
    next.set("tglimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Debounce search input → filters
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput
          ? prev
          : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchTags(filters);
      setRows(items);
      setPagination(pg);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load tags",
        message: mapped.message,
      });
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = () =>
    setDrawerState({ open: true, mode: "create", id: null });

  const openEdit = useCallback(
    (record) => setDrawerState({ open: true, mode: "edit", id: record.id }),
    []
  );

  const closeDrawer = () => setDrawerState((s) => ({ ...s, open: false }));

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Delete tag?",
        icon: <ExclamationCircleOutlined />,
        content: (
          <p style={{ marginTop: 0 }}>
            Are you sure you want to delete the tag{" "}
            <b>"{record.tag}"</b>? This cannot be undone.
          </p>
        ),
        okText: "Delete",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: async () => {
          const snapshot = rows;
          setRows((prev) => prev.filter((r) => r.id !== record.id));
          try {
            await apiHelpers.deleteTag(record.id);
            triggerNotification({
              type: "success",
              title: "Deleted",
              message: `Tag "${record.tag}" was deleted.`,
            });
            // If page becomes empty and we're not on page 1, go back one page
            const remainingOnPage = snapshot.length - 1;
            if (remainingOnPage === 0 && filters.page > 1) {
              setFilters((prev) => ({ ...prev, page: prev.page - 1 }));
            } else {
              fetchData();
            }
          } catch (err) {
            setRows(snapshot);
            const mapped = apiHelpers.mapError(err);
            Modal.error({
              title: mapped.code === "REFERENCED" ? "Cannot delete tag" : "Delete failed",
              content: (
                <div style={{ marginTop: 4 }}>
                  {mapped.message.split("|").map((part, i) => (
                    <div key={i} style={{ marginBottom: i < mapped.message.split("|").length - 1 ? 4 : 0 }}>
                      {part.trim()}
                    </div>
                  ))}
                </div>
              ),
              okText: "Close",
              centered: true,
              maskClosable: false,
              width: 480,
            });
          }
        },
      });
    },
    [rows, fetchData, filters.page]
  );

  const handleTableChange = useCallback((paginationInfo, _filters, sorter) => {
    const newPage = paginationInfo?.current || 1;
    if (sorter?.columnKey) {
      const newSortBy = sorter.columnKey;
      const newSortOrder = sorter.order === "ascend" ? "asc" : "desc";
      setFilters((prev) => ({
        ...prev,
        sort_by: newSortBy,
        sort_order: newSortOrder,
        page: prev.sort_by !== newSortBy ? 1 : newPage,
      }));
    } else {
      setFilters((prev) => ({ ...prev, page: newPage }));
    }
  }, []);

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: openEdit,
        onDelete: handleDelete,
        sortBy: filters.sort_by,
        sortOrder: filters.sort_order,
        page: filters.page,
        limit: filters.limit,
      }),
    [openEdit, handleDelete, filters.sort_by, filters.sort_order, filters.page, filters.limit]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Input
              allowClear
              placeholder="Search by tag"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 280 }}
            />
            <Select
              value={filters.limit}
              onChange={(v) =>
                setFilters((prev) => ({ ...prev, limit: v, page: 1 }))
              }
              options={PAGE_SIZE_OPTIONS.map((n) => ({
                value: n,
                label: `${n} / page`,
              }))}
              style={{ width: 120 }}
            />
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Tag
          </Button>
        </Space>
      </div>

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                description={
                  filters.search ? (
                    <span>No tags found for your search.</span>
                  ) : (
                    <span>
                      No tags found. Click <b>Add Tag</b> to create one.
                    </span>
                  )
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.totalPages || 1}
              </Typography.Text>
            ),
          }}
          bordered
          scroll={{ x: 800, y: 500 }}
        />
      </div>

      <TagFormDrawer
        open={drawerState.open}
        mode={drawerState.mode}
        tagId={drawerState.id}
        onClose={closeDrawer}
        onSaved={() => {
          closeDrawer();
          fetchData();
        }}
      />
    </>
  );
};

export default TagsTable;
